'use client';

import useTouch from '@/hooks/useTouch';
import cx from 'classnames';
import { FC, useState } from 'react';
import { AnimationFeatureAccountManagementProps } from './AnimationFeatureAccountManagement';
import { AnimationFeatureHelpDeskProps } from './AnimationFeatureHelpDesk';
import { AnimationFeatureSharedInboxProps } from './AnimationFeatureSharedInbox';
import Link from './Link';

export interface HighlightedFeatureTile {
  backgroundColor: 'citron' | 'violet' | 'blue';
  title: string;
  description: string;
  cta?: {
    title: string;
    href: string;
  };
  featureImage: React.ReactNode;

  animationComponent:
  | FC<AnimationFeatureHelpDeskProps>
  | FC<AnimationFeatureSharedInboxProps>
  | FC<AnimationFeatureAccountManagementProps>;
}

export interface HomeFeatureGridProps {
  title: React.ReactNode;
  topMargin: string,

  highlightedFeatures: HighlightedFeatureTile[];

  features: {
    icon: React.ReactNode;
    cta: {
      title: string;
      href: string;
    };
    description: string;
  }[];

  cta: {
    title: string;
    href: string;
  };
}

const customLinkClick = (url: string) => {
  window.location.href = url;
}

const HighlightedFeatureTile: FC<HighlightedFeatureTile> = ({
  backgroundColor,
  title,
  description,
  cta,
  animationComponent,
  featureImage,
}) => {
  const isTouch = useTouch();
  const [isHovering, setHovering] = useState(false);

  const Animation = animationComponent;

  const renderedAnimation = <Animation playing={isTouch || isHovering} />;

  return (

    <div
      onClick={() => customLinkClick(cta?.href ? cta.href : "/")}
      className={cx('rounded-10 flex flex-col justify-between gap-4 overflow-hidden hover-arrow-wrapper hover:cursor-pointer', {
        'bg-citron-500 text-blue-900': backgroundColor === 'citron',
        'bg-violet-300 text-blue-900': backgroundColor === 'violet',
        'bg-blue-200 text-blue-900': backgroundColor === 'blue',
      })}
    >
      <div className="pt-3 px-3 lg:px-4 lg:pt-5 flex flex-col gap-[12px]">
        <h3 className="font-500 tracking-tight text-h3 lg:-mr-1 text-blue-900">{title}</h3>
        <p className="text-p1 text-violet-900">{description}</p>
        {cta && (
          <div className="flex">
            <span className="flex font-500 text-p2 text-blue-900 items-center">
              {cta.title} <div className="flex ml-[8px] relative w-[16px] h-[16px] bg-[url(https://front.com/webassets/rebrand/black-right-arrows.png)] hover-arrow" />
            </span>
          </div>
        )}
      </div>
      {featureImage}
    </div>

  );
};

const HomeFeatureGrid: FC<HomeFeatureGridProps> = ({ title, topMargin, highlightedFeatures, features, cta }) => {
  const highlightedFeaturesGrid = (
    <div className="grid lg:grid-cols-3 gap-3 md:gap-gutter-desktop">
      {highlightedFeatures.map((feature, index) => (
        <HighlightedFeatureTile key={`highlighted-feature-${feature.title?.replace(/\s/g, '-') ?? index}`} {...feature} />
      ))}
    </div>
  );

  const featuresGrid = (
    <div className="grid gap-y-6 md:gap-y-4 grid-cols-2 md:grid-cols-4 gap-x-gutter-mobile md:gap-x-gutter-desktop text-gray-900 lg:px-2">
      {features.map((feature) => (
        <div
          key={`feature-${feature.cta.href}`}
          className="relative p-[12px] duration-200 ease-in-out-quad hover:bg-gray-100 hover:bg-opacity-50 rounded-10"
        >
          <div className="flex flex-col gap-2">
            <div className="flex flex-col gap-2 md:gap-1 md:flex-row md:items-center">
              {feature.icon}
              <p className="font-500 tracking-tight text-20/24">
                <Link href={feature.cta.href} className="link-cover">
                  {feature.cta.title}
                </Link>
              </p>
            </div>
            <p className="text-17/24 text-gray-700">{feature.description}</p>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className={`my-6 md:my-[132px] xl:my-${topMargin ? topMargin : "[132px]"} px-container-mobile md:px-container-desktop`}>
      <div className="mx-auto max-w-grid flex flex-col gap-8">
        <div className="flex justify-center items-center">
          <h2 className="font-500 tracking-tight text-30/42 md:text-43/52 md:text-center max-w-98">{title}</h2>
        </div>
        {highlightedFeaturesGrid}
        {featuresGrid}
      </div>
    </div>
  );
};

export default HomeFeatureGrid;
